import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { TrendingDown, Users, Award } from "lucide-react";

const HeroSection = () => {
  const [animatedNumbers, setAnimatedNumbers] = useState({
    contracts: 0,
    savings: 0,
    experience: 0,
  });

  const targetNumbers = {
    contracts: 2500,
    savings: 25,
    experience: 32,
  };

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    const intervals = [];

    Object.keys(targetNumbers).forEach((key) => {
      const target = targetNumbers[key];
      const increment = target / steps;
      let current = 0;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        current = Math.min(increment * step, target);
        setAnimatedNumbers((prev) => ({
          ...prev,
          [key]: Math.floor(current),
        }));

        if (step >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);

      intervals.push(interval);
    });

    return () => intervals.forEach(clearInterval);
  }, []);

  const scrollToCalculator = () => {
    const element = document.querySelector("#calculator");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="home" className="pt-36 pb-20 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <div className="space-y-10">
            <div className="space-y-8">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                EASY POWER :{" "}
                <span className="text-green-600">Votre courtier expert</span> en
                économie d'énergie
              </h1>
              
              <p className="text-2xl text-gray-600 leading-relaxed">
                Spécialisés en optimisation énergétique, nous aidons entreprises et
                particuliers à réduire leurs factures d'électricité et de gaz grâce à
                des contrats sur-mesure.
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-6">
              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border-green-100 hover:bg-white transition-colors duration-300 shadow-lg">
                <div className="flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {animatedNumbers.contracts}+
                </div>
                <div className="text-base text-gray-600 font-medium">
                  contrats optimisés
                </div>
              </Card>

              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border-emerald-100 hover:bg-white transition-colors duration-300 shadow-lg">
                <div className="flex items-center justify-center mb-4">
                  <TrendingDown className="h-8 w-8 text-emerald-600" />
                </div>
                <div className="text-4xl font-bold text-emerald-600 mb-2">
                  {animatedNumbers.savings}%
                </div>
                <div className="text-base text-gray-600 font-medium">
                  d'économies en moyenne
                </div>
              </Card>

              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border-green-100 hover:bg-white transition-colors duration-300 shadow-lg">
                <div className="flex items-center justify-center mb-4">
                  <Award className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {animatedNumbers.experience}
                </div>
                <div className="text-base text-gray-600 font-medium">
                  ans d'expérience
                </div>
              </Card>
            </div>

            <div className="bg-green-50 p-8 rounded-2xl border-l-4 border-green-600">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Basé en France</strong>, EASY POWER est un cabinet de courtage
                spécialisé en économie d'énergie. Nous mettons notre expertise au
                service des entreprises, collectivités et particuliers pour leur
                permettre de réduire durablement leur facture énergétique.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6">
              <Button
                onClick={scrollToCalculator}
                size="lg"
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-10 py-5 text-xl font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Calculer mes économies
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
                className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-10 py-5 text-xl font-semibold rounded-xl transition-all duration-300"
              >
                Audit gratuit
              </Button>
            </div>
          </div>

          {/* Right Column - Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU4MDM2MTkzfDA&ixlib=rb-4.1.0&q=85"
                alt="Salle de réunion moderne EASY POWER"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/20 to-transparent"></div>
            </div>
            
            {/* Floating Card */}
            <Card className="absolute -bottom-8 -left-8 p-6 bg-white shadow-2xl border-0 rounded-2xl">
              <div className="flex items-center space-x-4">
                <div className="bg-emerald-100 p-3 rounded-xl">
                  <TrendingDown className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">-1,200€/an</div>
                  <div className="text-gray-600">Économies moyennes</div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-10 px-12 rounded-3xl shadow-2xl">
            <h3 className="text-3xl font-bold mb-4">
              Profitez de notre expertise pour réduire durablement votre facture énergétique !
            </h3>
            <p className="text-xl text-green-100">
              Analyse gratuite • Sans engagement • Résultats garantis
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;